//@flow
import {
  CircularProgress,
  IconButton,
  MenuItem,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {useHistory, useParams} from 'react-router-dom';
import {FormProvider, useForm} from 'react-hook-form';
import React, {useEffect, useRef} from 'react';
import {ArrowForwardIos} from '@material-ui/icons';

import {colors} from '../../../../assets/styles/colors';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import IosSwitch from '../../../../elements/IosSwitch/IosSwitch';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import {E_INVOICE_FORM_FIELDS} from './constants';
import {useTaxInvoiceActions, useTaxInvoiceDetail} from '../../utils';

import {useStoreCountry} from '../../../country/utils';
import {useAdminAddress} from '../../../address/utils/useAdminAddress';
import {getConfigurationAdmin} from '../../../../utils/location';
import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';
import {sanitizeAlphanumeric} from '../../../../utils/string';

const useStyles = makeStyles(theme => ({
  root: {
    background: 'white',
    borderRadius: '.75rem',
    padding: '1rem',
    boxShadow:
      '0px 0.5px 2px 0px rgba(96, 97, 112, 0.16), 0px 0px 1px 0px rgba(40, 41, 61, 0.08);',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: '#ffffff69',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    cursor: 'progress',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    paddingBottom: '1rem',
    borderBottom: `1px solid ${colors.grey300}`,
  },
  backButton: {
    transform: 'rotate(180deg)',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
  },
  content: {
    background: colors.grey100,
    borderRadius: '.5rem',
    border: `1px solid ${colors.grey300}`,
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '.875rem',
    color: colors.grey900,
  },
  sectionSubtitle: {
    color: colors.grey800,
    fontSize: '.875rem',
  },
  documentList: {
    display: 'flex',
    flexDirection: 'column',
  },
  documentItem: {
    listStyle: 'inside',
    padding: '.5rem',
    fontWeight: 500,
    color: colors.grey800,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '1rem',
    borderBottom: `1px solid ${colors.grey300}`,
  },
  inputSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    width: '100%',
  },
  label: {
    fontSize: '.875rem',
    fontWeight: 500,
  },
  legalSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  inputContainers: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',

    [theme.breakpoints.up('md')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
    },
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: '1fr 1fr 1fr',
    },
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'center',
    marginBottom: '2rem',
    maxWidth: '200px',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
  usageSection: {
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    background: colors.grey100,
    borderRadius: '.5rem',
    border: `1px solid ${colors.grey300}`,
    gap: '1rem',

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
  usageDivider: {
    height: '1px',
    width: '100%',
    background: colors.grey300,
    [theme.breakpoints.up('sm')]: {
      height: '100%',
      width: '1px',
    },
  },
  usageItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    flexGrow: 1,
    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
    },
  },
  usageTitle: {
    fontSize: '.875rem',
    fontWeight: 600,
  },
  dateLabel: {
    fontSize: '.875rem',
    padding: '.5rem',
    borderRadius: '.75rem',
    background: colors.grey200,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '.5rem',
    flexGrow: 1,
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    fontWeight: 500,
    flexGrow: 1,
  },
  progress: {
    height: '.75rem',
    width: '100%',
    borderRadius: '1rem',
  },
  footerSection: {
    padding: '.75rem',
    background: 'white',
    borderRadius: '.5rem',
    border: `1px solid ${colors.grey300}`,
    display: 'flex',
    gap: '.5rem',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

export default function TaxInvoiceForm() {
  const classes = useStyles();

  const history = useHistory();
  const {slug: providerId} = useParams();

  const country = useStoreCountry();

  const {invoiceTypes, provider} = useTaxInvoiceDetail(providerId || '');

  const {
    createStoreProvider,
    loading: creatingTaxInvoice,
  } = useTaxInvoiceActions();

  const methods = useForm({});
  const {register, setValue, clearErrors, watch} = methods;

  const addressAlreadySetRef = useRef(false);

  const {
    admin1List,
    admin2List,
    admin3List,
    selectedAdmin1,
    selectedAdmin2,
    selectedAdmin3,
    selectAdmin2,
    selectAdmin3,
    selectAdmin1,
    loading,
  } = useAdminAddress();

  useEffect(() => {
    register(E_INVOICE_FORM_FIELDS.enableAutoEmission);
    register(E_INVOICE_FORM_FIELDS.enabled);
    register(E_INVOICE_FORM_FIELDS.companyName);
    register(E_INVOICE_FORM_FIELDS.ruc);
    register(E_INVOICE_FORM_FIELDS.address);
    register(E_INVOICE_FORM_FIELDS.admin1);
    register(E_INVOICE_FORM_FIELDS.admin2);
    register(E_INVOICE_FORM_FIELDS.admin3);
  }, [register]);

  useEffect(() => {
    if (!provider) return;

    // set default values
    const providerConfig = provider.providerConfig;

    if (!providerConfig) return;

    setValue(E_INVOICE_FORM_FIELDS.enabled, providerConfig.enabled);
    setValue(
      E_INVOICE_FORM_FIELDS.enableAutoEmission,
      !!providerConfig?.automaticGeneration
    );
    setValue(
      E_INVOICE_FORM_FIELDS.companyName,
      providerConfig.taxTransmitterName
    );
    setValue(E_INVOICE_FORM_FIELDS.ruc, providerConfig.taxTransmitterCode);

    setValue(
      E_INVOICE_FORM_FIELDS.address,
      providerConfig.storeTaxInvoiceAddress?.address ||
        providerConfig.storetaxinvoiceaddress?.address
    );
  }, [provider, setValue]);

  useEffect(() => {
    // set the default address
    const alreadySet = addressAlreadySetRef.current;
    if (alreadySet) return;
    if (!provider) return;

    const providerConfig = provider.providerConfig;

    if (!providerConfig) return;

    const admin1 = admin1List.find(
      admin =>
        admin.adminId === providerConfig.storeTaxInvoiceAddress?.admin1 ||
        providerConfig.storetaxinvoiceaddress?.admin1 === admin.adminId
    );

    if (!admin1) return;

    selectAdmin1(admin1);
    setValue(E_INVOICE_FORM_FIELDS.admin1, admin1.adminId);

    const admin2 = admin2List.find(
      admin =>
        admin.adminId === providerConfig.storeTaxInvoiceAddress?.admin2 ||
        providerConfig.storetaxinvoiceaddress?.admin2 === admin.adminId
    );

    if (!admin2) return;

    selectAdmin2(admin2);
    setValue(E_INVOICE_FORM_FIELDS.admin2, admin2.adminId);

    const admin3 = admin3List.find(
      admin =>
        admin.adminId === providerConfig.storeTaxInvoiceAddress?.admin3 ||
        providerConfig.storetaxinvoiceaddress?.admin3 === admin.adminId
    );

    if (!admin3) return;

    selectAdmin3(admin3);
    setValue(E_INVOICE_FORM_FIELDS.admin3, admin3.adminId);

    addressAlreadySetRef.current = true;
  }, [
    admin1List,
    admin2List,
    admin3List,
    provider,
    selectAdmin1,
    selectAdmin2,
    selectAdmin3,
    setValue,
  ]);

  const handleBack = () => {
    history.goBack();
  };
  const handleSave = data => {
    if (!providerId) return;

    createStoreProvider({
      automatic_generation: !!data[E_INVOICE_FORM_FIELDS.enableAutoEmission],
      enabled: !!data[E_INVOICE_FORM_FIELDS.enabled],
      store_tax_invoice_types: invoiceTypes.map(invoiceType => {
        return {
          enabled: !!data[E_INVOICE_FORM_FIELDS.enabled],
          country: invoiceType.country.code,
          tax_invoice_type_id: invoiceType.id,
        };
      }),
      tax_invoice_provider_id: providerId,
      tax_transmitter_code: data[E_INVOICE_FORM_FIELDS.ruc],
      tax_transmitter_name: data[E_INVOICE_FORM_FIELDS.companyName],
      store_tax_invoice_address: {
        address:
          sanitizeAlphanumeric(data[E_INVOICE_FORM_FIELDS.address]) || '',
        country: country ? country.code : '',
        admin1: data[E_INVOICE_FORM_FIELDS.admin1],
        admin2: data[E_INVOICE_FORM_FIELDS.admin2],
        admin3: data[E_INVOICE_FORM_FIELDS.admin3],
      },
    }).then(success => {
      if (!success) return;
      history.push('/integrations/tax-invoice/settings');
    });
  };

  if (!provider) return null;

  const configAdmin = getConfigurationAdmin(country?.code);

  const watchCompanyName = watch(E_INVOICE_FORM_FIELDS.companyName);
  const watchRuc = watch(E_INVOICE_FORM_FIELDS.ruc);
  const watchAddress = watch(E_INVOICE_FORM_FIELDS.address);
  const watchEnableAutoEmission = watch(
    E_INVOICE_FORM_FIELDS.enableAutoEmission
  );
  const watchEnabled = watch(E_INVOICE_FORM_FIELDS.enabled);

  return (
    <FormProvider {...methods}>
      <form className={classes.root}>
        {creatingTaxInvoice && (
          <div className={classes.loader}>
            <CircularProgress color="primary" size={50} />
          </div>
        )}

        <header className={classes.header}>
          <IconButton className={classes.backButton} onClick={handleBack}>
            <ArrowForwardIos />
          </IconButton>

          <Typography className={classes.title}>{provider.name}</Typography>
        </header>

        <div className={classes.content}>
          <header className={classes.sectionHeader}>
            <div className={classes.sectionHeaderData}>
              <Typography className={classes.sectionTitle}>
                Facturación Electrónica
              </Typography>
              <Typography className={classes.sectionSubtitle}>
                Configura los permisos de tus comprobantes de pago
              </Typography>
            </div>

            <div className={classes.options}>
              <IosSwitch
                onChange={event => {
                  setValue(E_INVOICE_FORM_FIELDS.enabled, event.target.checked);
                }}
                checked={!!watchEnabled}
              />
            </div>
          </header>

          <div className={classes.documentList}>
            {invoiceTypes.map(document => (
              <li key={document.id} className={classes.documentItem}>
                {document.label}
              </li>
            ))}
          </div>

          <div className={classes.legalSection}>
            <div className={classes.inputContainers}>
              <div className={classes.inputSection}>
                <span className={classes.label}>Razón Social</span>
                <CustomTextField
                  name={E_INVOICE_FORM_FIELDS.companyName}
                  required
                  inputRef={methods.register({required: 'Campo requerido'})}
                  value={watchCompanyName}
                />
              </div>
              <div className={classes.inputSection}>
                <span className={classes.label}>RUC</span>
                <CustomTextField
                  name={E_INVOICE_FORM_FIELDS.ruc}
                  required
                  inputRef={methods.register({required: 'Campo requerido'})}
                  value={watchRuc}
                />
              </div>

              <div className={classes.inputSection}>
                <span className={classes.label}>Dirección Fiscal</span>
                <CustomTextField
                  name={E_INVOICE_FORM_FIELDS.address}
                  required
                  inputRef={methods.register({required: 'Campo requerido'})}
                  value={watchAddress}
                />
              </div>

              <CustomSelect
                value={selectedAdmin1?.adminName || ''}
                name={E_INVOICE_FORM_FIELDS.admin1}
                label={configAdmin.admin1.label}
                onChange={e => {
                  const adminOption = admin1List.find(
                    admin1 => admin1.adminName === e.target.value
                  );
                  if (!adminOption) return;
                  setValue(E_INVOICE_FORM_FIELDS.admin1, adminOption.adminId);
                  clearErrors(E_INVOICE_FORM_FIELDS.admin1);
                  selectAdmin1(adminOption);
                }}
                disabled={loading || admin1List.length === 0}
              >
                {admin1List.map((adminAddress, index: number) => (
                  <MenuItem
                    key={adminAddress.adminId}
                    value={adminAddress.adminName}
                  >
                    {adminAddress.adminName}
                  </MenuItem>
                ))}
              </CustomSelect>

              <CustomSelect
                value={selectedAdmin2?.adminName || ''}
                name={E_INVOICE_FORM_FIELDS.admin2}
                label={configAdmin.admin2.label}
                onChange={e => {
                  const adminOption = admin2List.find(
                    adminAddress => adminAddress.adminName === e.target.value
                  );
                  if (!adminOption) return;
                  setValue(E_INVOICE_FORM_FIELDS.admin2, adminOption.adminId);
                  clearErrors(E_INVOICE_FORM_FIELDS.admin2);
                  selectAdmin2(adminOption);
                }}
                disabled={loading || admin2List.length === 0}
              >
                {admin2List.map((adminAddress, index: number) => (
                  <MenuItem
                    key={adminAddress.adminId}
                    value={adminAddress.adminName}
                  >
                    {adminAddress.adminName}
                  </MenuItem>
                ))}
              </CustomSelect>

              <CustomSelect
                value={selectedAdmin3?.adminName || ''}
                name={E_INVOICE_FORM_FIELDS.admin3}
                label={configAdmin.admin3.label}
                onChange={e => {
                  const adminOption = admin3List.find(
                    adminAddress => adminAddress.adminName === e.target.value
                  );
                  if (!adminOption) return;
                  setValue(E_INVOICE_FORM_FIELDS.admin3, adminOption.adminId);
                  clearErrors(E_INVOICE_FORM_FIELDS.admin3);
                  selectAdmin3(adminOption);
                }}
                disabled={loading || admin3List.length === 0}
              >
                {admin3List.map((adminAddress, index: number) => (
                  <MenuItem
                    key={adminAddress.adminId}
                    value={adminAddress.adminName}
                  >
                    {adminAddress.adminName}
                  </MenuItem>
                ))}
              </CustomSelect>
            </div>

            <footer className={classes.footerSection}>
              <div className={classes.footerData}>
                <Typography className={classes.sectionTitle}>
                  Emitir automáticamente cuando la orden cambie de estado a
                  pagado
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Si esta función es desactivada lo tendrá que hacer de forma
                  manual.
                </Typography>
              </div>
              <div className={classes.options}>
                <IosSwitch
                  onChange={event => {
                    setValue(
                      E_INVOICE_FORM_FIELDS.enableAutoEmission,
                      event.target.checked
                    );
                  }}
                  checked={!!watchEnableAutoEmission}
                />
              </div>
            </footer>
          </div>
        </div>

        {/*         <div className={classes.usageSection}>
          <div className={classes.usageItem}>
            <Typography className={classes.usageTitle}>
              Facturas Emitidas
            </Typography>
            <div className={classes.progressContainer}>
              <LinearProgress
                value={50}
                variant="determinate"
                color="primary"
                className={classes.progress}
              />
              <span>120/200</span>
            </div>
          </div>
          <span className={classes.usageDivider}></span>
          <div className={classes.usageItem}>
            <Typography className={classes.usageTitle}>
              Fecha de restock
            </Typography>
            <div className={classes.dateLabel}>
              <CalendarToday />
              <Typography>
                <Hidden xsUp>Siguiente fecha de pago: </Hidden>14/06/23
              </Typography>
            </div>
          </div>
        </div> */}

        <footer className={classes.footer}>
          <CustomButton
            variant="contained"
            fullWidth
            type="submit"
            onClick={methods.handleSubmit(handleSave)}
          >
            Guardar
          </CustomButton>
          {/*           <CustomButton variant="text" fullWidth>
            Cambiar integración
          </CustomButton> */}
        </footer>
      </form>
    </FormProvider>
  );
}
